import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { Router } from '@angular/router';

import { OnboardingCelebrationConfig, OnboardingTheme } from '../../interfaces/onboarding.interfaces';

/**
 * Modal de celebración al completar una fase del onboarding. Presentacional + auto-hospedado (mismo
 * patrón que `fina-eligible-modal`): se monta en el template del Home y el contenedor lo abre con
 * `open(config, theme)`. El contenido es 100% data-driven (`OnboardingCelebrationConfig`) y respeta el
 * tema de la fase (`purple` | `green`).
 */
@Component({
    selector: 'fina-onboarding-celebration-modal',
    templateUrl: './onboarding-celebration-modal.component.html',
    styleUrls: ['./onboarding-celebration-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingCelebrationModalComponent {
    @Input() config?: OnboardingCelebrationConfig;
    @Input() theme: OnboardingTheme = 'purple';

    /** Se emite cuando el modal se cierra (CTA principal/secundario o ESC). Lo consume el shell para la
     *  instrumentación `onboarding_completed` (cierre del embudo en la última fase). */
    @Output() closed = new EventEmitter<void>();

    /** Métrica única (tareas completadas / total de la fase), tomada del `state$` real. */
    tasksDone = 0;
    tasksTotal = 0;

    isVisible = false;

    constructor(private readonly router: Router, private readonly cdr: ChangeDetectorRef) {}

    open(config: OnboardingCelebrationConfig, theme: OnboardingTheme = 'purple', tasksDone = 0, tasksTotal = 0): void {
        this.config = config;
        this.theme = theme;
        this.tasksDone = tasksDone;
        this.tasksTotal = tasksTotal;
        this.isVisible = true;
        this.cdr.markForCheck();
    }

    close(): void {
        if (!this.isVisible) return; // ya cerrado → no re-emitir `closed`
        this.isVisible = false;
        this.cdr.markForCheck();
        this.closed.emit();
    }

    /** ESC cierra el modal (overlay propio, no NgbModal). Guard: solo si está visible. */
    @HostListener('document:keydown.escape')
    onEscKeydown(): void {
        if (this.isVisible) this.close();
    }

    onPrimaryClick(): void {
        const link = this.config?.primary_cta?.link;
        this.close();
        if (link) {
            this.router.navigateByUrl(link);
        }
    }

    onSecondaryClick(): void {
        this.close();
    }
}
